import React, { useState } from 'react';
import styles from './ReservationInput.module.css';
import agodaLogo from '../../assets/images/AgodaLogo.png';
import pricelineLogo from '../../assets/images/PricelineLogo.png';
import { Formik, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import ErrorInput from '../errorInput/ErrorInput';
import FormInput from '../formInput/FormInput';

interface IShowReservationElements {
  showCustomerReservation: boolean;
  showReservationAssistance: boolean;
  showAgodaBrandLink: boolean;
  showPricelineBrandLink: boolean;
  showReservationAssistanceNumber: string;
}

interface IReservationInput {
  showAccountLockout: boolean;
  showErrorText: boolean;
  errorMessage: string;
  errorHeader: string;
  remainingAttempts: string;
  reservationElementsConfig: IShowReservationElements;
  getBookingReservationDetailsHandler: (reservationId: string, pinNumber: string) => void;
}

interface IOnSubmitInput {
  setSubmitting: (flag: boolean) => void;
}

interface FormValues {
  reservationId: string;
  pinNumber: string;
}

const ReservationInput = ({
  showAccountLockout,
  showErrorText,
  errorMessage,
  errorHeader,
  remainingAttempts,
  reservationElementsConfig,
  getBookingReservationDetailsHandler
}: IReservationInput) => {
  const { t } = useTranslation();
  const [showErrorFlag, setShowErrorFlag] = useState(true);
  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};
    setShowErrorFlag(false);
    if (!values.reservationId) {
      errors.reservationId = 'isEmpty';
    } else if (!/^(?=(?:\D*\d){10}\D*$)[\d\s.]+$/i.test(values.reservationId.toString())) {
      errors.reservationId = 'isInvalid';
    }
    if (!values.pinNumber) {
      errors.pinNumber = 'isEmpty';
    } else if (!/^[0-9a-zA-Z]{4}$/i.test(values.pinNumber.toString())) {
      errors.pinNumber = 'isInvalid';
    }
    return errors;
  };

  const onSubmit = async (values: FormValues, { setSubmitting }: IOnSubmitInput) => {
    getBookingReservationDetailsHandler(
      values.reservationId.toString().replace(/\s/g, '').replace(/\./g, ''),
      values.pinNumber
    );
    setShowErrorFlag(true);
    setSubmitting(false);
  };

  return (
    <div className={styles.reservationContainer}>
      {showAccountLockout && (
        <ErrorInput
          errorMessage={errorMessage}
          messageHeader={errorHeader}
          remainingAttempts={remainingAttempts}
        />
      )}
      {reservationElementsConfig.showCustomerReservation && (
        <section aria-label="How to access your reservation">
          <div className={styles.reservationInputContainer} data-testid="reservationInputDiv">
            <div className={styles.reservationHeading}>
              <h1 id="accessReservation" className={styles.reservationInputHeadingText}>
                <strong className={styles.reservationInputHeading}>
                  {t('traveller_reservation_input_heading')}
                </strong>
              </h1>
              <p>{t('reservationHeading')}</p>
            </div>
            <p className={styles.reservationInputFormImportantText}>
              <strong>{t('right_important_traveller_reservation_label')}</strong>
            </p>
            {showErrorFlag && showErrorText && (
              <ErrorInput
                errorMessage={errorMessage}
                messageHeader={errorHeader}
                remainingAttempts={remainingAttempts}
              />
            )}
            <Formik
              initialValues={{ reservationId: '', pinNumber: '' }}
              validate={validate}
              onSubmit={onSubmit}>
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting
              }) => (
                <form
                  autoComplete="off"
                  className={styles.reservationInputForm}
                  onSubmit={handleSubmit}>
                  <FormInput
                    label={t('reservationInputContainer_label')}
                    id="reservationId"
                    name="reservationId"
                    placeholder={`${t('Example')}: 999 999 999 ....`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.reservationId}
                    errorType={errors.reservationId}
                    touched={touched.reservationId}
                  />
                  <FormInput
                    label={t('reservationInputContainer_pin')}
                    id="pinNumber"
                    name="pinNumber"
                    placeholder={`${t('Example')}: 1234...`}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.pinNumber}
                    errorType={errors.pinNumber}
                    touched={touched.pinNumber}
                  />
                  <button type="submit" disabled={isSubmitting}>
                    {t('reservationInputContainer_button')}
                  </button>
                </form>
              )}
            </Formik>
          </div>
        </section>
      )}
      {reservationElementsConfig.showReservationAssistance && (
        <div className={styles.bookingDottedLine} />
      )}
      {reservationElementsConfig.showReservationAssistance && (
        <section aria-label="Get help by phone">
          <div className={styles.bookingCustomerService} data-testid="bookingCustomerServiceDiv">
            <div className={styles.bookingAssistance}>
              <h2 className={styles.reservationInputHeadingText}>
                <strong className={styles.reservationInputHeading}>
                  {t('reservation_booking_assistance_heading')}
                </strong>
              </h2>
              <p>
                {t('reservationBookingAssistance_1')}{' '}
                <strong>{t('reservationBookingAssistance_2')}</strong>{' '}
                {t('reservationBookingAssistance_3')}{' '}
                <strong>{t('reservationBookingAssistance_4')}</strong>
                {t('reservationBookingAssistance_5')}
              </p>
            </div>
            <div className={styles.bookingCustomerNumber}>
              <p>{reservationElementsConfig.showReservationAssistanceNumber}</p>
            </div>
            <div className={styles.bookingLanguageSupport}>
              <p>{t('bookingLanguageSupport')}</p>
            </div>
          </div>
        </section>
      )}

      {(reservationElementsConfig.showAgodaBrandLink ||
        reservationElementsConfig.showPricelineBrandLink) && (
        <section aria-label="Make a new reservation">
          <div className={styles.reservationFooter} data-testid="reservationFooterDiv">
            <h2 className={styles.reservationFooterHeading}>
              <strong>{t('reservation_footer_heading')}</strong>
            </h2>
            <span>{t('reservationFooter')}</span>
            <div className={styles.bookingSistersBrand}>
              {reservationElementsConfig.showAgodaBrandLink && (
                <div className={styles.agodaBrandContainer}>
                  <p>{t('reservation_agoda_heading')}</p>
                  <a href="http://bcomtravel.agoda.com/">
                    <img className={styles.agodaLogo} src={agodaLogo} alt="agodaLogo" />
                  </a>
                </div>
              )}
              {reservationElementsConfig.showPricelineBrandLink && (
                <div className={styles.pricelineBrandContainer}>
                  <p>{t('reservation_priceline_heading')}</p>
                  <a href="http://crdr.booking.com/priceline">
                    <img className={styles.pricelineLogo} src={pricelineLogo} alt="pricelineLogo" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
export default ReservationInput;
